import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { apiGet, apiPost } from '@/lib/api';
import { useUIStore } from '@/stores/uiStore';
import socketService from '@/lib/socket';

// Available skills
export const AVAILABLE_SKILLS = [
  {
    id: 'TFL',
    name: 'skill:TFL',
    description: 'Review Tables, Figures, and Listings',
  },
  {
    id: 'CSR-1',
    name: 'skill:CSR Generation',
    description: 'Generate Clinical Study Reports',
  },
  {
    id: 'CSR-2',
    name: 'skill:CSR Review',
    description: 'Review Clinical Study Reports',
  },
  {
    id: 'Define-1',
    name: 'skill:Define Review',
    description: 'Review Define.xml',
  },
  {
    id: 'Define-2',
    name: 'skill:Define Generation',
    description: 'Generate Define.xml',
  },
  {
    id: 'SDTM-2',
    name: 'skill:SDTM Generation',
    description: 'Generate SDTM Datasets',
  },
] as const;

// Board step from API / WS
export interface BoardStep {
  name: string;
  description?: string;
  startedAt?: string; // ISO date string
  error?: string;
  finishedAt?: string; // ISO date string
  status?: string; // step-level status: cancelled / running / done etc.
}

// Board state for a conversation
export interface BoardState {
  conversationId: string;
  steps: BoardStep[];
  stepCount?: number; // 🔥 后端返回的总步骤数（优先使用，比 steps.length 更准确）
  status?: string; // 🔥 board 整体状态：running / waiting / done 等
  fetchedAt: Date; // when we fetched from API
  pausedAt?: number; // 🔥 暂停/waiting 状态时的时间戳，用于冻结计时显示
}

// board_state WS event payload
export interface BoardStepEvent {
  type: string; // e.g. 'board_step_end', 'board_step_start', 'board_step_error'
  conversationId: string;
  index: number;
  name?: string;
  description?: string;
  startedAt?: string;
  finishedAt?: string;
  error?: string;
  stepCount?: number; // 🔥 后端返回的总步骤数
  status?: string; // 🔥 board 整体状态：running / waiting / done 等
}

interface SkillTaskState {
  // State
  boards: Record<string, BoardState>; // conversationId -> board state
  isDashboardOpen: boolean;
  unreadCount: number;

  // Actions
  fetchBoard: (conversationId: string) => Promise<void>;
  setBoardSteps: (conversationId: string, steps: BoardStep[]) => void;
  updateBoardStep: (event: BoardStepEvent) => void;
  setDashboardOpen: (open: boolean) => void;
  clearUnreadCount: () => void;
  removeBoard: (conversationId: string) => void;
  cancelBoard: (conversationId: string) => Promise<boolean>;
  getBoard: (conversationId: string) => BoardState | undefined;
  getAllBoards: () => BoardState[];
}

export const useSkillTaskStore = create<SkillTaskState>()(
  devtools(
    (set, get) => ({
      boards: {},
      isDashboardOpen: false,
      unreadCount: 0,

      fetchBoard: async (conversationId: string) => {
        // 🔥 temp- 开头的 conversationId 是前端临时 ID，后端不认识，跳过
        if (conversationId.startsWith('temp-')) {
          console.log(
            '🎯 [Board] Skipping fetchBoard for temp ID:',
            conversationId
          );
          return;
        }
        try {
          console.log('🎯 [Board] Fetching board for:', conversationId);
          const response = await apiGet<{
            code: number;
            message: string;
            conversationId: number | string;
            steps: BoardStep[];
            stepCount?: number;
            status?: string;
          }>(`/api/ai/conversations/${conversationId}/board`);

          if (response.code === 200 && response.steps) {
            // 🔥 从 step 的 status 推导 board 整体状态
            // 优先使用后端返回的 board-level status，否则从 steps 推导
            let boardStatus = response.status;
            if (!boardStatus) {
              // 🔥 从 step 的 status 推导 board 整体状态
              const hasCancelledStep = response.steps.some(
                s => s.status === 'cancelled'
              );
              const hasWaitingStep = response.steps.some(
                s => s.status === 'waiting'
              );
              if (hasCancelledStep) {
                boardStatus = 'cancelled';
              } else if (hasWaitingStep) {
                // 🔥 如果有步骤标记为 waiting，board 整体状态也应为 waiting
                boardStatus = 'waiting';
              } else {
                // 🔥 推导 waiting 状态：有已完成的步骤、没有正在运行的步骤、但还有未开始的步骤
                // 例如 API 返回 steps=[{finished}], stepCount=4 → 说明 board 在等待下一步
                const hasFinishedStep = response.steps.some(
                  s => s.finishedAt && !s.error
                );
                const hasRunningStep = response.steps.some(
                  s => s.startedAt && !s.finishedAt && !s.error
                );
                const totalExpected =
                  response.stepCount ?? response.steps.length;
                const allStepsFinished =
                  response.steps.length > 0 &&
                  response.steps.every(s => s.finishedAt);
                // 有完成的步骤 + 没有运行中的步骤 + 还有更多步骤待执行 → waiting
                if (
                  hasFinishedStep &&
                  !hasRunningStep &&
                  totalExpected > response.steps.length
                ) {
                  boardStatus = 'waiting';
                } else if (
                  allStepsFinished &&
                  response.steps.length === totalExpected &&
                  totalExpected > 0
                ) {
                  // 所有步骤都完成了 → done
                  boardStatus = 'done';
                }
              }
            }
            console.log('🎯 [Board] API response status:', {
              conversationId,
              responseStatus: response.status,
              derivedStatus: boardStatus,
              stepStatuses: response.steps.map(s => s.status).filter(Boolean),
            });

            // 🔥 如果 board 已取消，给未完成的步骤补上 finishedAt，防止时间继续增长
            let processedSteps = response.steps;
            if (boardStatus === 'cancelled') {
              const now = new Date().toISOString();
              processedSteps = response.steps.map(s => {
                if (s.startedAt && !s.finishedAt) {
                  return { ...s, finishedAt: now, status: 'cancelled' };
                }
                return s;
              });
            }

            set(state => ({
              boards: {
                ...state.boards,
                [conversationId]: {
                  conversationId,
                  steps: processedSteps,
                  stepCount: response.stepCount,
                  status: boardStatus,
                  fetchedAt: new Date(),
                  // 🔥 如果 board 是 waiting 状态，设置 pausedAt 冻结计时器
                  ...(boardStatus === 'waiting'
                    ? { pausedAt: Date.now() }
                    : {}),
                },
              },
            }));

            // 🔥 如果返回了 board 数据（有 steps），说明是 skill 对话
            // 自动注册为 skill 对话（刷新浏览器后恢复 skill 状态）
            if (response.steps.length > 0) {
              const uiState = useUIStore.getState();
              if (!uiState.isSkillConversation(conversationId)) {
                uiState.addSkillConversation(conversationId);
                console.log(
                  '🎯 [Board] Auto-registered as skill conversation:',
                  conversationId
                );
              }
              // 🔥 打开 Tasks Status 面板，显示历史步骤
              set({ isDashboardOpen: true, unreadCount: 0 });
            }

            // 🔥 自动加入 WS 房间（独立于 steps.length 判断）
            // 即使 steps 为空（pending 状态，任务已提交但未开始），也需要加入 WS 房间
            // 否则 board_step_start 事件永远收不到，conversation 永远停在 pending
            const isTerminalBoard =
              boardStatus === 'cancelled' ||
              boardStatus === 'error' ||
              boardStatus === 'done' ||
              (processedSteps.length > 0 &&
                processedSteps.every(s => s.finishedAt));
            if (!isTerminalBoard) {
              if (socketService.isSocketConnected()) {
                socketService.emit('join_conversation_room', conversationId);
                console.log(
                  '🎯 [Board] Auto-joined WS room for active/pending board:',
                  conversationId
                );
              } else {
                // 🔥 Socket 尚未连接，等待连接后加入房间
                console.log(
                  '🎯 [Board] Socket not connected yet, will join room on reconnect:',
                  conversationId
                );
              }
            }

            console.log('🎯 [Board] Board fetched:', {
              conversationId,
              stepsCount: response.steps.length,
            });
          }
        } catch (error) {
          console.error('❌ [Board] Failed to fetch board:', error);
        }
      },

      setBoardSteps: (conversationId, steps) =>
        set(state => ({
          boards: {
            ...state.boards,
            [conversationId]: {
              conversationId,
              steps,
              fetchedAt: state.boards[conversationId]?.fetchedAt || new Date(),
            },
          },
        })),

      updateBoardStep: (event: BoardStepEvent) =>
        set(state => {
          // 🔥 如果 board 不存在，自动创建一个空 board（避免 WS 事件因 fetchBoard 未完成而丢失）
          const board = state.boards[event.conversationId] || {
            conversationId: event.conversationId,
            steps: [],
            fetchedAt: new Date(),
          };

          const updatedSteps = [...board.steps];
          const idx = event.index;

          // Ensure the steps array is large enough
          while (updatedSteps.length <= idx) {
            updatedSteps.push({ name: '' });
          }

          const existingStep = updatedSteps[idx];

          if (event.type === 'board_step_start') {
            updatedSteps[idx] = {
              ...existingStep,
              name: event.name || existingStep.name,
              description: event.description || existingStep.description,
              startedAt: event.startedAt || new Date().toISOString(),
              finishedAt: undefined,
              error: undefined,
            };
          } else if (event.type === 'board_step_end') {
            updatedSteps[idx] = {
              ...existingStep,
              name: event.name || existingStep.name,
              finishedAt: event.finishedAt || new Date().toISOString(),
            };
          } else if (event.type === 'board_step_error') {
            updatedSteps[idx] = {
              ...existingStep,
              name: event.name || existingStep.name,
              error: event.error || 'Unknown error',
              finishedAt: event.finishedAt || new Date().toISOString(),
            };
          } else if (event.type === 'board_step_status') {
            // 🔥 board_step_status：更新 board 的整体 status（waiting/running/cancelled）
            console.log('🎯 [Board] Status updated:', {
              conversationId: event.conversationId,
              status: event.status,
            });

            let stepsForStatus = board.steps;
            let pausedAt = board.pausedAt;

            if (event.status === 'cancelled') {
              // 🔥 取消时，给所有正在运行的步骤补上 finishedAt，彻底冻结时间
              const now = new Date().toISOString();
              stepsForStatus = board.steps.map(s => {
                if (s.startedAt && !s.finishedAt) {
                  return { ...s, finishedAt: now, status: 'cancelled' };
                }
                return s;
              });
              pausedAt = undefined;
            } else if (event.status === 'waiting') {
              // 🔥 waiting 状态：记录暂停时间戳，供组件冻结计时显示
              if (!pausedAt) {
                pausedAt = Date.now();
              }
            } else {
              // running 或其他状态：清除暂停时间戳
              pausedAt = undefined;
            }

            return {
              boards: {
                ...state.boards,
                [event.conversationId]: {
                  ...board,
                  steps: stepsForStatus,
                  pausedAt,
                  ...(event.stepCount !== undefined
                    ? { stepCount: event.stepCount }
                    : {}),
                  ...(event.status !== undefined
                    ? { status: event.status }
                    : {}),
                },
              },
            };
          } else {
            // Generic update
            updatedSteps[idx] = {
              ...existingStep,
              ...(event.name ? { name: event.name } : {}),
              ...(event.startedAt ? { startedAt: event.startedAt } : {}),
              ...(event.finishedAt ? { finishedAt: event.finishedAt } : {}),
              ...(event.error ? { error: event.error } : {}),
            };
          }

          console.log('🎯 [Board] Step updated:', {
            conversationId: event.conversationId,
            type: event.type,
            index: idx,
            name: updatedSteps[idx].name,
          });

          // 🔥 判断 board 的 status 和 pausedAt 更新
          // board_step_start 无条件将 waiting 转为 running（事件本身就意味着恢复执行）
          // board_step_end / board_step_error 不应覆盖 waiting 状态
          const isStartEvent = event.type === 'board_step_start';
          const wasWaiting = board.status === 'waiting';

          // 计算新的 status
          let newStatusUpdate: Record<string, any> = {};
          if (isStartEvent && wasWaiting) {
            // 🔥 board_step_start + board 是 waiting → 强制转为 running（不依赖 event.status）
            newStatusUpdate = {
              status: event.status ?? 'running',
              pausedAt: undefined,
            };
          } else if (isStartEvent && event.status !== undefined) {
            // board_step_start + 非 waiting + 有 status → 更新 status
            newStatusUpdate = { status: event.status };
          } else if (!wasWaiting && event.status !== undefined) {
            // 非 waiting 状态 + 有 status → 正常更新
            newStatusUpdate = { status: event.status };
          }
          // waiting + 非 board_step_start → 不更新 status（保护 waiting 状态）

          return {
            boards: {
              ...state.boards,
              [event.conversationId]: {
                ...board,
                steps: updatedSteps,
                // 🔥 更新 stepCount（如果事件中包含）
                ...(event.stepCount !== undefined
                  ? { stepCount: event.stepCount }
                  : {}),
                ...newStatusUpdate,
              },
            },
          };
        }),

      setDashboardOpen: open =>
        set(() => {
          if (open) {
            return { isDashboardOpen: open, unreadCount: 0 };
          }
          return { isDashboardOpen: open };
        }),

      clearUnreadCount: () => set({ unreadCount: 0 }),

      removeBoard: conversationId =>
        set(state => {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { [conversationId]: _, ...rest } = state.boards;
          return { boards: rest };
        }),

      cancelBoard: async (conversationId: string) => {
        try {
          console.log('🎯 [Board] Cancelling board for:', conversationId);
          const response = await apiPost<{ code: number; message: string }>(
            `/api/ai/conversations/${conversationId}/board/cancel`
          );
          if (response.code === 200) {
            console.log('🎯 [Board] Board cancelled:', conversationId);
            // 🔥 立即更新本地状态：标记为 cancelled，冻结所有运行中步骤的时间
            const now = new Date().toISOString();
            set(state => {
              const board = state.boards[conversationId];
              if (!board) return state;
              const frozenSteps = board.steps.map(s => {
                if (s.startedAt && !s.finishedAt) {
                  return { ...s, finishedAt: now, status: 'cancelled' };
                }
                return s;
              });
              return {
                boards: {
                  ...state.boards,
                  [conversationId]: {
                    ...board,
                    steps: frozenSteps,
                    status: 'cancelled',
                    pausedAt: undefined,
                  },
                },
              };
            });
            return true;
          }
          console.error('❌ [Board] Cancel failed:', response.message);
          return false;
        } catch (error) {
          console.error('❌ [Board] Failed to cancel board:', error);
          return false;
        }
      },

      getBoard: conversationId => get().boards[conversationId],

      getAllBoards: () => Object.values(get().boards),
    }),
    { name: 'SkillTaskStore' }
  )
);

// 🔥 Socket 重连时，自动重新加入所有活跃 board 的 WS 房间
// 始终注册 connect 事件，确保网络断开重连后能恢复房间订阅
const _setupSocketReconnectHandler = () => {
  const socket = socketService.getSocket();
  if (socket) {
    socket.on('connect', () => {
      // 🔥 从 store 中获取所有非终态的 board，重新加入 WS 房间
      const boards = useSkillTaskStore.getState().boards;
      Object.values(boards).forEach(board => {
        const isTerminal =
          board.status === 'cancelled' ||
          board.status === 'error' ||
          board.status === 'done' ||
          (board.steps.length > 0 && board.steps.every(s => s.finishedAt));
        if (!isTerminal) {
          socketService.emit('join_conversation_room', board.conversationId);
          console.log(
            '🎯 [Board] Socket reconnected -> rejoined WS room:',
            board.conversationId
          );
        }
      });
    });
  }
};
_setupSocketReconnectHandler();
